import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { hasMenuPermission } from '@/utils/menu-filter';
import type { UserPermission } from '@/types/permission';
import type { GlobalState } from '@/store';

export type IRoute = {
  name: string;
  key: string;
  // 当前页是否展示面包屑
  breadcrumb?: boolean;
  children?: IRoute[];
  // 当前路由是否渲染菜单项，为 true 的话不会在菜单中显示，但可通过路由地址访问。
  ignore?: boolean;
};

export const routes: IRoute[] = [
  // 工作台
  {
    name: 'menu.dashboard',
    key: 'dashboard/workplace',
  },
  // 实时监控（隐藏页面，不在菜单中显示）
  {
    name: 'menu.dashboard.monitor',
    key: 'dashboard/monitor',
    ignore: true,
  },
  // HRBP 出勤工时页（隐藏页面，不在菜单中显示）
  {
    name: 'menu.dashboard.hrbpAttendance',
    key: 'dashboard/hrbp-attendance',
    ignore: true,
  },
  // 总监资源详情页（隐藏页面，不在菜单中显示）
  {
    name: 'menu.dashboard.resources',
    key: 'dashboard/resources',
    ignore: true,
  },
  // 商业项目管理
  {
    name: 'menu.projects',
    key: 'projects',
    children: [
      {
        name: 'menu.projects.list',
        key: 'projects/list',
      },
      {
        name: 'menu.projects.create',
        key: 'projects/create',
        ignore: true,
      },
      {
        name: 'menu.projects.change',
        key: 'projects/change',
        ignore: true,
      },
      {
        name: 'menu.projects.pendingEntry',
        key: 'projects/pending-entry',
        ignore: true,
      },
      {
        name: 'menu.projects.frameworks',
        key: 'frameworks/list',
      },
      {
        name: 'menu.projects.frameworksCreate',
        key: 'frameworks/create',
        ignore: true,
      },
    ],
  },
  // 售前支持
  {
    name: 'menu.presales',
    key: 'presales/list',
  },
  // 专业贡献
  {
    name: 'menu.contributions',
    key: 'contributions/list',
  },
  // 任务和工时管理
  {
    name: 'menu.tasks',
    key: 'tasks',
    children: [
      {
        name: 'menu.tasks.management',
        key: 'tasks/index',
      },
      {
        name: 'menu.tasks.calendar',
        key: 'tasks/calendar',
        breadcrumb: false, // 不显示面包屑
      },
    ],
  },
  // 待办中心（审批管理）
  {
    name: 'menu.approvals',
    key: 'approval-center',
    children: [
      {
        name: 'menu.approvals.list',
        key: 'approval-center/index',
        ignore: true,
      },
      {
        name: 'menu.approvals.detail',
        key: 'approval-center/:id',
        ignore: true,
      },
    ],
  },
  // 业绩管理
  {
    name: 'menu.performance',
    key: 'performance/list',
  },
  // 成本管理
  {
    name: 'menu.costs',
    key: 'costs',
    children: [
      {
        name: 'menu.costs.labor',
        key: 'costs/labor/list',
      },
      {
        name: 'menu.costs.travel',
        key: 'costs/travel/list',
      },
      {
        name: 'menu.costs.outsource',
        key: 'costs/outsource/list',
      },
    ],
  },
  // 报表中心
  {
    name: 'menu.reports',
    key: 'reports',
    children: [
      {
        name: 'menu.reports.signedPerformance',
        key: 'reports/signed-performance',
      },
      {
        name: 'menu.reports.completedPerformance',
        key: 'reports/completed-performance',
      },
      {
        name: 'menu.reports.acceptancePerformance',
        key: 'reports/acceptance-performance',
      },
      {
        name: 'menu.reports.developmentHours',
        key: 'reports/hours-report',
      },
      {
        name: 'menu.reports.developmentHoursDrilldown',
        key: 'reports/hours-report/drilldown',
        ignore: true,
      },
    ],
  },
  // 系统设置
  {
    name: 'menu.settings',
    key: 'settings',
    children: [
      {
        name: 'menu.settings.users',
        key: 'settings/users',
      },
      {
        name: 'menu.settings.systemPermissions',
        key: 'settings/system-permissions',
      },
      {
        name: 'menu.settings.projectPermissions',
        key: 'settings/project-permissions',
      },
      {
        name: 'menu.settings.cost',
        key: 'settings/cost',
      },
      {
        name: 'menu.settings.approval',
        key: 'settings/approval',
      },
      {
        name: 'menu.settings.other',
        key: 'settings/other',
        children: [
          {
            name: 'menu.settings.other.clientDepartments',
            key: 'settings/other/client-departments',
            ignore: true,
          },
          {
            name: 'menu.settings.other.suppliers',
            key: 'settings/other/suppliers',
            ignore: true,
          },
          {
            name: 'menu.settings.other.stageTemplates',
            key: 'settings/other/stage-templates',
            ignore: true,
          },
          {
            name: 'menu.settings.other.approvalProcessTemplates',
            key: 'settings/other/approval-process-templates',
            ignore: true,
          },
          {
            name: 'menu.settings.other.workday',
            key: 'settings/other/workday',
            ignore: true,
          },
          {
            name: 'menu.settings.other.taskTypes',
            key: 'settings/other/task-types',
            ignore: true,
          },
          {
            name: 'menu.settings.other.otherTasksConfig',
            key: 'settings/other/other-tasks-config',
            ignore: true,
          },
        ],
      },
    ],
  },
];

export const getName = (path: string, routes) => {
  return routes.find((item) => {
    const itemPath = `/${item.key}`;
    if (path === itemPath) {
      return item.name;
    } else if (item.children) {
      return getName(path, item.children);
    }
  });
};

export const generatePermission = (role: string) => {
  const actions = role === 'admin' ? ['*'] : ['read'];
  const result = {};
  routes.forEach((item) => {
    if (item.children) {
      item.children.forEach((child) => {
        result[child.name] = actions;
      });
    }
  });
  return result;
};

const EMPTY_ROLE_LIST: Array<{ code: string }> = [];

function isRouteTreeEqual(a: IRoute[], b: IRoute[]): boolean {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i += 1) {
    const aRoute = a[i];
    const bRoute = b[i];
    if (
      aRoute.key !== bRoute.key ||
      aRoute.name !== bRoute.name ||
      Boolean(aRoute.ignore) !== Boolean(bRoute.ignore) ||
      Boolean(aRoute.breadcrumb) !== Boolean(bRoute.breadcrumb)
    ) {
      return false;
    }

    const aChildren = aRoute.children || [];
    const bChildren = bRoute.children || [];
    if (!isRouteTreeEqual(aChildren, bChildren)) {
      return false;
    }
  }

  return true;
}

const useRoute = (
  userPermission: UserPermission,
  isPermissionLoading = false
): [IRoute[], string] => {
  const userRoles = useSelector(
    (state: GlobalState) => state.userInfo?.roles || EMPTY_ROLE_LIST
  );
  const userRoleCodes = useMemo(() => userRoles.map((item) => item.code), [userRoles]);

  const filterRoute = (routes: IRoute[], arr = []): IRoute[] => {
    if (!routes.length) {
      return [];
    }
    for (const route of routes) {
      // 跳过 ignore 为 true 的路由
      if (route.ignore) {
        continue;
      }

      // 使用完整的 route.key 来检查权限
      const visible = hasMenuPermission(route.key, userPermission || {}, userRoleCodes);

      if (route.children && route.children.length) {
        // 对于有子菜单的路由，先递归过滤子菜单
        const newRoute = { ...route, children: [] };
        filterRoute(route.children, newRoute.children);
        // 如果有可见的子菜单，或者父菜单本身有权限，则显示父菜单
        if (newRoute.children.length || visible) {
          arr.push(newRoute);
        }
      } else {
        // 对于没有子菜单的路由，只有权限检查通过才显示
        if (visible) {
          arr.push({ ...route });
        }
      }
    }

    return arr;
  };

  const [permissionRoute, setPermissionRoute] = useState(routes);

  useEffect(() => {
    if (isPermissionLoading) {
      return;
    }
    const newRoutes = filterRoute(routes);
    setPermissionRoute((prev) => (isRouteTreeEqual(prev, newRoutes) ? prev : newRoutes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPermission, userRoleCodes, isPermissionLoading]);

  const defaultRoute = useMemo(() => {
    const first = permissionRoute[0];
    if (first) {
      const firstRoute = first?.children?.[0]?.key || first.key;
      return firstRoute;
    }
    return '';
  }, [permissionRoute]);

  return [permissionRoute, defaultRoute];
};

export default useRoute;
